import React, { useCallback } from 'react';

import type { AddressType } from '@sg/garnish';
import { useSafePathname, useSafeRouter } from '@sg/garnish';
import type { DeliveryOrderDetailInput } from '@sg/graphql-schema';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useNavigateToMainTabs } from '../../navigation';
import { useDeliveryOrderInFlight } from '../useDeliveryOrderInFlight';

import type { MenuScreenReferrer } from '../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavigateToMenu = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();
  const pathname = useSafePathname();
  const navigateToMainTabs = useNavigateToMainTabs();

  const shouldDismissAll = !pathname?.includes('/locations');

  const handleGoToMenu = React.useCallback(
    async (restaurantSlug: string, referrer?: MenuScreenReferrer, category?: string) => {
      if (shouldDismissAll && isExpoRouterEnabled && router?.canDismiss()) {
        router.dismissAll();
      }

      if (isExpoRouterEnabled) {
        router?.navigate({
          pathname: '/[restaurantSlug]/menu',
          params: { restaurantSlug, ...(category ? { category } : {}) },
        });
        return;
      }

      return navigateToMainTabs('MenuTab', {
        screen: 'Menu',
        params: { restaurantSlug, referrer, category },
      });
    },
    [shouldDismissAll, isExpoRouterEnabled, router, navigateToMainTabs],
  );

  const handleGoToDeliveryMenu = React.useCallback(
    async (addressId: string, referrer?: MenuScreenReferrer, category?: string) => {
      if (shouldDismissAll && isExpoRouterEnabled && router?.canDismiss()) {
        router.dismissAll();
      }

      if (isExpoRouterEnabled) {
        router?.navigate({
          pathname: '/delivery/[addressId]/menu',
          params: { addressId, ...(category ? { category } : {}) },
        });
        return;
      }

      return navigateToMainTabs('MenuTab', {
        screen: 'DeliveryMenu',
        params: { addressId, referrer, category },
      });
    },
    [shouldDismissAll, isExpoRouterEnabled, router, navigateToMainTabs],
  );

  // ─── In Flight Delivery Orders ───────────────────────────────────

  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  return useCallback(
    ({
      orderType,
      restaurantSlug = '',
      deliveryOrderDetail = undefined,
      referrer,
      category,
    }: UseNavigateToMenuProps) => {
      if (['pickup', 'outpost'].includes(orderType)) {
        void handleGoToMenu(restaurantSlug, referrer, category);

        return;
      }

      // Block going to delivery menu when there is an in-flight delivery order.
      if (inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      const deliveryType = deliveryOrderDetail as Readonly<{
        address: { id: string };
      }>;
      const addressId = deliveryOrderDetail?.addressId ?? deliveryType?.address?.id;

      if (orderType === 'delivery' && addressId) {
        void handleGoToDeliveryMenu(addressId, referrer, category);
      }
    },
    [
      inFlightDeliveryOrderId,
      handleGoToDeliveryMenu,
      handleGoToMenu,
      navigateToDeliveryOrderInFlightScreen,
    ],
  );
};

export type UseNavigateToMenuProps = Readonly<{
  orderType: AddressType;
  restaurantId?: string;
  restaurantSlug?: string;
  restaurantName?: string;
  deliveryAddressName?: string;
  deliveryOrderDetail?: Partial<DeliveryOrderDetailInput>;
  referrer?: MenuScreenReferrer;
  category?: string;
}>;
